<section>
  <header>
    <h1 auto="eval-step-1-title">Evaluating Phase</h1>
  </header>
  <div class="information-container">
    <div>
      <p><strong>Determine Impact of Critical Initiatives</strong></p>
      <p>
        There still is a need for summative review even though elements of the Evaluating Phase appear throughout the entire strategic thinking and improvement planning process. To address this, the Evaluating Phase leverages the theories of action you created to determine the effectiveness of each Critical Initiative. These determinations ultimately inform your discussions about the effectiveness of the Annual Improvement Plan and any implications for future work.
      </p>
      <p>
        To begin this work, select "View Theory of Action" to access the theory of action for each Critical Initiative and complete the following steps:
      </p>
      <p>
        1. Determine the results of the Intended Outcomes and Key Measures for each Critical Initiative.<br>
        2. Verify the Critical Initiative was implemented with fidelity.<br>
        3. Discuss findings from Critical Initiatives in relation to one another and describe results in a holistic way.<br>
      </p>
      <p>
        Understanding the progress of the Annual Improvement Plan should inform decision-making for the future. You can document the findings in a narrative format in the space provided below. This information will remain here, but you might want to save it in another format for future use. You also can upload documents that help substantiate your findings.
      </p>
    </div>
  </div>
  <div class="explanation-margin">
    <div class="download-guide-container">
      <img class="book-icon pointer" src="assets/images/download-guide-icon.svg" alt="book-icon" (click)="openGuide()">
      <span class="download-tagline pointer" (click)="openGuide()">Download the guide</span> to learn more about <strong>Determine Impact of Critical Initiatives</strong>.
    </div>
  </div>
  <!-- Initiative Selector Section -->
  <div class="select-initiative-header"><strong>Select a Critical Initiative:</strong></div>
  <div class="selection-container">
    <div class="input-error-wrap sans-margin">
      <div class="form-row">
        <div class="control-container">
          <app-priority-dropdown
            [showDates]="true"
            [singleSelection]="false"
            [availableOptions]="selectOptions"
            (valueChangedCallback)="onDropdownSelect($event)"
            [styleClassAppends]="['default-selector-width']"
            defaultLabel="Select Critical Initiative(s)"
            selectedItemsLabel="Critical Initiatives Selected ({0})"
            selectAllItemsLabel="Select All Critical Initiatives"
            allItemsSelectedLabel="All Critical Initiatives Selected ({0})">
          </app-priority-dropdown>
          <button class="btn btn-primary init-select-btn"
                  [disabled]="!selectOptions || selectOptions.length === 0 || !selectedOptions || selectedOptions.length === 0"
                  (click)="displaySelectedInitiatives()">View</button>
        </div>
      </div>
    </div>
  </div>
  <!-- Initiative Content Section -->
  <div *ngIf="filteredInitiatives && filteredInitiatives.length > 0"
        class="dici-initiative-container">
    <div *ngFor="let initiative of filteredInitiatives"
          class="dici-initiative-row">
      <div class="dici-initiative-text-content-container">
        <!-- Left Side Content -->
        <div class="dici-initiative-row-left-content">
          <h5 class="ci-label">Critical Initiative</h5>
          <ng-container *ngIf="!initiative.analysis">
            <div class="expansion-text-container">
              <i class="pi pi-caret-down expansion-icon" *ngIf="doesExpandedLocationMatch(initiative.initiativeId)" (click)="resetExpandedInitiative()"></i>
              <p class="hard-break-text">{{initiative.description}}</p>
            </div>
          </ng-container>
          <ng-container *ngIf="initiative.analysis">
            <div class="expansion-text-container">
              <i class="pi pi-caret-right expansion-icon" *ngIf="!doesExpandedLocationMatch(initiative.initiativeId)" (click)="showAnalysis(initiative)"></i>
              <i class="pi pi-caret-down expansion-icon" *ngIf="doesExpandedLocationMatch(initiative.initiativeId)" (click)="resetExpandedInitiative()"></i>
              <p (click)="toggleInitiativeExpansion(initiative)" class="expansion-text hard-break-text">{{initiative.description}}</p>
            </div>
          </ng-container>
        </div>
        <!-- Right Side Content -->
        <div class="dici-initiative-row-right-content">
          <button type="button" auto="view-theory-action-btn" class="btn btn-primary toa-button" (click)="onClickViewTheoryOfAction(initiative)">View Theory of Action</button>
          <button type="button"
                  class="btn btn-primary add-analysis-btn"
                  (click)="showAnalysisInput(initiative)"
                  pTooltip="Add Analysis"
                  tooltipPosition="bottom"
                  appendTo="body"
                  [disabled]="(initiative.analysis && initiative.analysis.text) || (expandedLocation$ | async)?.initiative === initiative.initiativeId">
            <i class="eleot-icon icon-add" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <!-- Default Hidden Secondary Content -->
      <ng-container *ngIf="expandedLocation$ | async as location">
        <!-- Create / Edit Mode -->
        <div class="dici-initiative-row-expanded-content" *ngIf="location.initiative === initiative.initiativeId && (location.isCreate || location.isEdit)">
          <div class="analysis-header-container">
            <h5 class="analysis-header">Analysis of Findings</h5>
          </div>
          <app-rich-content editorTitle=""
                            placeholderText="Enter Analysis of Findings here"
                            [content]="initiative.analysis ? initiative.analysis.text || '' : ''"
                            [maxContentLength]="3000"
                            [isReadOnly]="isEditItemDisabled"
                            (contentUpdate)="onAnalysisChanged($event)"
                            [autoFocus]="true"
                            [forceEdit]="true"
                            [updateTimeOverride]="1">
          </app-rich-content>
          <div class="analysis-button-row">
            <button auto="edit-user-cancle-btn" class="btn btn-alt left-btn" type="button" (click)="resetExpandedInitiative()">Cancel</button>
            <button auto="edit-user-save-btn" class="btn" type="submit" [disabled]="location.analysis ? location.analysis.text === null : true" (click)="onAnalysisSave()">Save</button>
          </div>
        </div>
        <!-- View Mode -->
        <div class="dici-initiative-row-expanded-content" *ngIf="location.initiative === initiative.initiativeId && !location.isEdit && !location.isCreate">
          <div class="analysis-header-container">
            <div class="analysis-action-container">
              <h5 class="analysis-last-user">{{initiative.analysis.lastUpdatedUserFirstName}} {{initiative.analysis.lastUpdatedUserLastName}}</h5>
              <p class="analysis-last-modified"><i>{{initiative.analysis.lastUpdatedDate}}</i></p>
              <button type="button"
                      class="btn btn-primary edit-btn"
                      (click)="showAnalysisInput(initiative, true)"
                      pTooltip="Edit Analysis"
                      tooltipPosition="bottom"
                      appendTo="target">
                <i class="fa fa-pencil" aria-hidden="true"></i>
              </button>
              <button type="button"
                      class="btn btn-primary"
                      (click)="deleteAnalysis(initiative)"
                      pTooltip="Edit Analysis"
                      tooltipPosition="bottom"
                      appendTo="target">
                <i class="fa fa-trash" aria-hidden="true"></i>
              </button>
            </div>
            <h5 class="analysis-header">Analysis of Findings</h5>
          </div>
          <div class="analysis-view-text" [innerHtml]="safeText(initiative.analysis.text)">
          </div>
        </div>
      </ng-container>
    </div>
  </div>


  <ng-container *ngIf="(isLoading$ | async) === false">
    <ng-container *ngIf="!planHasPriorities">
      <div class="empty-message">
        <h1>
          <span class="text">To begin this subphase, complete the Envisioning phase first.</span>
        </h1>
      </div>
    </ng-container>
    <ng-container *ngIf="planHasPriorities && !planHasInitiatives">
      <div class="empty-message">
        <h1>
          <span class="text">In order to begin this subphase, complete the work in your previous subphases.</span>
        </h1>
      </div>
    </ng-container>
  </ng-container>
</section>

<p-dialog #comfirmDeleteAnalysisDialog
          styleClass="delete-modal"
          [modal]="true"
          [closable]="false"
          [draggable]="false"
          [visible]="analysisDeleteActive"
          appendTo="body">
  <p-header class="delete-modal-header">
    <img src="assets/images/exclamation-white.svg" alt="Alert">
    <span class="dialog-title">Delete Analysis</span>
  </p-header>
  <div><strong>Are you sure you want to delete this Analysis of Findings?</strong></div>
  <div>You are about to delete the analysis for the following Critical Initiative:</div>
  <div class="hard-break-text"><strong><i>{{initiativeToDeleteAnalysis ? initiativeToDeleteAnalysis.description : ''}}</i></strong></div>
  <div>Once this information is deleted, it can not be undone.</div>

  <p-footer>
    <button auto="cancel-btn" class="btn btn-alt cancel" type="button" pButton label="Cancel"
            (click)="resetAnalysisDelete()">
    </button>
    <button auto="delete-btn" class="btn delete" type="button" pButton label="Delete"
            (click)="onAnalysisDelete()"></button>
  </p-footer>
</p-dialog>
